if (!customElements.get("cart-upgrades")) {
  let savedUpgradesSliderState = {
    index: 0,
    translate: 0,
  };

  class CartUpgrades extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.checkboxes = [];
    }

    connectedCallback() {
      this.initSlider();
      this.initCheckboxes();
    }

    disconnectedCallback() {
      this.saveSliderState();
      this.destroySlider();
    }

    /* ---------- SLIDER ---------- */

    saveSliderState() {
      if (!this.swiper) return;
      savedUpgradesSliderState = {
        index: this.swiper.activeIndex || 0,
        translate: this.swiper.translate || 0,
      };
    }

    restoreSliderState() {
      if (!this.swiper) return;

      const { index, translate } = savedUpgradesSliderState;
      const maxIndex = Math.max(0, (this.swiper.slides?.length || 1) - 1);
      const safeIndex = Math.min(Math.max(0, index), maxIndex);

      requestAnimationFrame(() => {
        if (!this.swiper) return;

        if (typeof this.swiper.slideTo === "function") {
          this.swiper.slideTo(safeIndex, 0, false);
        }

        // freeMode: keep exact scroll position when possible
        if (typeof translate === "number" && typeof this.swiper.setTranslate === "function") {
          this.swiper.setTranslate(translate);
          this.swiper.updateProgress();
          this.swiper.updateActiveIndex();
          this.swiper.updateSlidesClasses();
        }
      });
    }

    initSlider() {
      const sliderEl = this.querySelector(".product-upgrades-slider");
      if (!sliderEl || typeof Swiper === "undefined") return;

      const nextEl = this.querySelector(".product-upgrades__arrow--next");
      const prevEl = this.querySelector(".product-upgrades__arrow--prev");
      const initialSlide = savedUpgradesSliderState.index || 0;

      this.swiper = new Swiper(sliderEl, {
        slidesPerView: 1.5,
        spaceBetween: 8,
        initialSlide,
        freeMode: {
          enabled: true,
        },
        cssMode: false,
        grabCursor: true,
        simulateTouch: true,

        navigation: {
          nextEl,
          prevEl,
        },

        mousewheel: {
          enabled: true,
          forceToAxis: true,
        },

        breakpoints: {
          768: {
            slidesPerView: 2,
            spaceBetween: 12,
          },
        },
        on: {
          init: () => this.restoreSliderState(),
        },
      });
    }

    destroySlider() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    /* ---------- CART / CHECKBOXES ---------- */

    async initCheckboxes() {
      this.checkboxes = Array.from(
        this.querySelectorAll(".product-upgrades__item-id")
      );
      if (!this.checkboxes.length) return;

      this.checkboxes.forEach((checkbox) => {
        const itemEl = checkbox.closest(".product-upgrades__item");

        this.toggleItemState(itemEl, checkbox.checked);

        checkbox.addEventListener("change", (event) =>
          this.onCheckboxChange(event)
        );
      });
    }

    toggleItemState(itemEl, isChecked) {
      if (!itemEl) return;
      itemEl.classList.toggle("product-upgrades__item--checked", isChecked);
    }

    async onCheckboxChange(event) {
      const checkbox = event.target;
      const variantId = checkbox.value;
      const itemEl = checkbox.closest(".product-upgrades__item");

      // Prefer keeping the interacted slide active after cart re-render
      if (itemEl && this.swiper) {
        const slideIndex = this.swiper.slides
          ? Array.from(this.swiper.slides).indexOf(itemEl)
          : -1;
        if (slideIndex >= 0) {
          savedUpgradesSliderState = {
            index: slideIndex,
            translate: this.swiper.translate || 0,
          };
        } else {
          this.saveSliderState();
        }
      }

      try {
        if (checkbox.checked) {
          await this.addToCart(variantId);
          this.toggleItemState(itemEl, true);
        } else {
          await this.removeFromCart(variantId);
          this.toggleItemState(itemEl, false);
        }

        document.dispatchEvent(
          new CustomEvent("cart:added", {
            detail: {
              item: { id: Number(variantId) },
            },
          })
        );
      } catch (err) {
        console.error("Cart update failed", err);
        checkbox.checked = !checkbox.checked;
        this.toggleItemState(itemEl, checkbox.checked);
      }
    }

    async fetchCart() {
      const res = await fetch("/cart.js", {
        headers: {
          Accept: "application/json",
        },
      });
      if (!res.ok) throw new Error("Cart fetch failed");
      return res.json();
    }

    async addToCart(variantId) {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
        }),
      });

      if (!res.ok) throw new Error("Add to cart failed");
      return res.json();
    }

    async removeFromCart(variantId) {
      const cart = await this.fetchCart();
      const item = cart.items.find(
        (item) => item.variant_id === Number(variantId)
      );

      if (!item) return;

      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: item.key,
          quantity: 0,
        }),
      });

      if (!res.ok) throw new Error("Remove from cart failed");
      return res.json();
    }
  }

  customElements.define("cart-upgrades", CartUpgrades);
}
